import { comparePriority } from "@restomenu/core/contract";
import type { MenuCategory, MenuProduct } from "@/types";

export interface SortByPrice {
  sort: "ASC" | "DESC";
  /** Осталось от серверной сортировки; клиентская его не читает. */
  pointBind?: { pointId: string; orderType: string };
}

export interface ActiveFilters {
  tagsInclude?: string[];
  tagsExclude?: string[];
  categoryId?: string;
  isActive?: boolean;
  sortByCategoryId?: string;
  sortByPrice?: SortByPrice;
}

interface GroupedProducts {
  category: MenuCategory;
  products: MenuProduct[];
}

/**
 * Группирует товары по категориям и сортирует внутри каждой.
 */
export const groupProductsByCategories = (
  products: MenuProduct[],
  categories: MenuCategory[],
): Record<string, GroupedProducts> => {
  const grouped: Record<string, GroupedProducts> = {};

  categories.forEach((category) => {
    grouped[category.id] = { category, products: [] };
  });

  products.forEach((product) => {
    product.categoryBinds.forEach((bind) => {
      if (grouped[bind.categoryId]) {
        grouped[bind.categoryId].products.push(product);
      }
    });
  });

  // productPriority — лексоранк, «позиция в алфавите сортировки, а не
  // число». Прежний parseInt("aZz") давал NaN и случайный порядок.
  Object.keys(grouped).forEach((categoryId) => {
    grouped[categoryId].products.sort((a, b) =>
      comparePriority(
        a.categoryBinds.find((bind) => bind.categoryId === categoryId)
          ?.priority ?? "",
        b.categoryBinds.find((bind) => bind.categoryId === categoryId)
          ?.priority ?? "",
      ),
    );
  });

  return grouped;
};

/**
 * Категории, в которых есть хотя бы один из переданных товаров.
 *
 * Прежняя версия складывала в Set результат `find`, то есть undefined для
 * неизвестной привязки, и порядок категорий терялся вместе с порядком
 * обхода Set. Считаем по идентификаторам и сохраняем порядок allCategories.
 */
export const getCategoriesByProducts = (
  products: MenuProduct[],
  allCategories: MenuCategory[],
): MenuCategory[] => {
  const ids = new Set<string>();
  products.forEach((product) => {
    product.categoryBinds.forEach((bind) => ids.add(bind.categoryId));
  });
  return allCategories.filter((category) => ids.has(category.id));
};

/**
 * Главный товар — тот, что показывается в списке. Группа вариантов и
 * обычный товар — всегда; одиночный вариант — только если помечен главным.
 */
export const isMainProduct = (product: MenuProduct): boolean => {
  if (product.type === "productVariantGroup") return true;
  if (product.type === "product") return true;
  return product.variantSettings?.isMain === true;
};

export const filterMainProducts = (products: MenuProduct[]): MenuProduct[] =>
  products.filter(isMainProduct);

/**
 * Поиск по загруженному списку.
 *
 * Клиентский не по недосмотру: `search` гостевому контуру не выведен
 * сознательно — «виджет ищет на клиенте по уже полученному списку» (схема).
 *
 * По id сравнение ТОЧНОЕ, а не подстрочное. Подстрочное было в
 * searchProductsSaga, но её результат в SearchModal и не показывался —
 * модалка читает ветку, которая на всём протяжении жизни кода сравнивала
 * id целиком. Разница не косметическая: запросу «8» удовлетворяет
 * подавляющее большинство uuid, и в выдачу поиска падало бы всё меню.
 */
export const searchProducts = (
  products: MenuProduct[],
  query: string,
): MenuProduct[] => {
  const needle = query.toLowerCase().trim();
  if (!needle) return [];

  return products.filter(
    (product) =>
      product.name.toLowerCase().includes(needle) ||
      product.description?.toLowerCase().includes(needle) ||
      product.productTags.some((tag) =>
        tag.name.toLowerCase().includes(needle),
      ) ||
      product.id.toLowerCase() === needle,
  );
};

/**
 * Фильтрация по тегам.
 *
 * Клиентская (решение 1 плана Ф4б): меню приезжает целиком одним запросом,
 * пагинации у контура нет, и серверные tagsIdAny/tagsIdNotAny дали бы
 * перезапрос с миганием списка на каждое переключение чекбокса.
 */
export const applyTagFilters = (
  products: MenuProduct[],
  filters: ActiveFilters,
): MenuProduct[] => {
  const include = filters.tagsInclude ?? [];
  const exclude = filters.tagsExclude ?? [];
  if (include.length === 0 && exclude.length === 0) return products;

  return products.filter((product) => {
    const tagIds = product.productTags.map((tag) => tag.id);
    if (include.length > 0 && !include.some((id) => tagIds.includes(id))) {
      return false;
    }
    if (exclude.length > 0 && exclude.some((id) => tagIds.includes(id))) {
      return false;
    }
    return true;
  });
};

/**
 * Сортировка по цене — клиентская, той же причиной, что и фильтры.
 *
 * Товар без цены уходит в конец при любом направлении: подставленный ноль
 * выносил бы его в ASC наверх и выдавал за самый дешёвый.
 */
export const sortProducts = (
  products: MenuProduct[],
  sortByPrice: SortByPrice | undefined,
): MenuProduct[] => {
  if (!sortByPrice) return products;

  const direction = sortByPrice.sort === "DESC" ? -1 : 1;
  return [...products].sort((a, b) => {
    if (a.pricePoint == null && b.pricePoint == null) return 0;
    if (a.pricePoint == null) return 1;
    if (b.pricePoint == null) return -1;
    return (a.pricePoint - b.pricePoint) * direction;
  });
};
